import * as THREE from "three";

// ---------------------------------------------------------------------------
// All four faces of the card are drawn into <canvas> elements at runtime.
// All text uses the AnishHand handwritten font (loaded by main.ts via the
// FontFace API) with a serif fallback. The two photos that are NOT on the
// cover render as separate 3D Polaroid meshes -- see Polaroid.ts and main.ts.
// ---------------------------------------------------------------------------

export const CARD = {
  outerTitle: "Happy Mother's Day",
  outerSub: "Mom \u2014 2026",
  insideTo: "To Mom,",
  insideMessage:
    "Thank you for every quiet sacrifice, every late-night phone call, " +
    "every reminder to eat real food, and every time you believed in me " +
    "before I knew how to. I love you more than I know how to say.",
  insideSign: "\u2014 Anish",
  hintBeforeOpen: "tap the card to open",
};

export interface CardTextureSet {
  outsideFront: THREE.Texture;
  insideLeft: THREE.Texture;
  insideRight: THREE.Texture;
  outsideBack: THREE.Texture;
}

export interface CardAssets {
  /** Photo to feature in the polaroid on the front cover. */
  coverPhoto: HTMLImageElement | null;
}

export function buildCardTextures(assets: CardAssets): CardTextureSet {
  return {
    outsideFront: makeOutsideFront(CARD.outerTitle, CARD.outerSub, assets.coverPhoto),
    insideLeft: makeInsideLeft(),
    insideRight: makeInsideRight(CARD.insideTo, CARD.insideMessage, CARD.insideSign),
    outsideBack: makeOutsideBack(),
  };
}

// ---------------------------------------------------------------------------
// Canvas helpers
// ---------------------------------------------------------------------------

const W = 1024;
const H = 1380;

const HAND = "'AnishHand', 'Georgia', cursive";

function newCanvas(): { c: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d")!;
  return { c, ctx };
}

function toTex(c: HTMLCanvasElement): THREE.Texture {
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

function paperBg(ctx: CanvasRenderingContext2D, base = "#fbf5e8") {
  const grad = ctx.createRadialGradient(W / 2, H / 2, W * 0.2, W / 2, H / 2, W * 0.85);
  grad.addColorStop(0, base);
  grad.addColorStop(1, shade(base, -0.12));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // grain
  for (let i = 0; i < 4500; i++) {
    ctx.fillStyle = `rgba(80, 60, 30, ${Math.random() * 0.04})`;
    ctx.fillRect(Math.random() * W, Math.random() * H, 1, 1);
  }
}

function shade(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.min(255, ((n >> 16) & 0xff) + Math.round(255 * amt)));
  const g = Math.max(0, Math.min(255, ((n >> 8) & 0xff) + Math.round(255 * amt)));
  const b = Math.max(0, Math.min(255, (n & 0xff) + Math.round(255 * amt)));
  return `rgb(${r}, ${g}, ${b})`;
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  align: CanvasTextAlign = "left",
): number {
  ctx.textAlign = align;
  const words = text.split(" ");
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  lines.forEach((l, i) => ctx.fillText(l, x, y + i * lineHeight));
  return lines.length * lineHeight;
}

/** drawImage with object-fit: cover semantics. */
function drawImageCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
) {
  const ir = img.width / img.height;
  const dr = dw / dh;
  let sx = 0;
  let sy = 0;
  let sw = img.width;
  let sh = img.height;
  if (ir > dr) {
    sw = img.height * dr;
    sx = (img.width - sw) / 2;
  } else {
    sh = img.width / dr;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

// ---------------------------------------------------------------------------
// 1. Outside front: burgundy backdrop with a hero polaroid + handwritten title
// ---------------------------------------------------------------------------

function makeOutsideFront(
  title: string,
  sub: string,
  photo: HTMLImageElement | null,
): THREE.Texture {
  const { c, ctx } = newCanvas();

  // Deep dusty-rose background with a soft radial highlight.
  const grad = ctx.createRadialGradient(W * 0.35, H * 0.4, 80, W / 2, H / 2, W);
  grad.addColorStop(0, "#a04767");
  grad.addColorStop(0.55, "#7a2f4d");
  grad.addColorStop(1, "#421421");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // very subtle linen grain
  for (let i = 0; i < 8000; i++) {
    ctx.fillStyle = `rgba(255,220,200,${Math.random() * 0.03})`;
    ctx.fillRect(Math.random() * W, Math.random() * H, 1, 1);
  }

  // Gold inner border (double rule)
  ctx.strokeStyle = "#e6c79a";
  ctx.lineWidth = 5;
  ctx.strokeRect(70, 70, W - 140, H - 140);
  ctx.lineWidth = 1.5;
  ctx.strokeRect(96, 96, W - 192, H - 192);

  // Title at the top
  ctx.fillStyle = "#f3dfb1";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `120px ${HAND}`;
  const titleY = 240;
  wrapText(ctx, title, W / 2, titleY, W - 260, 110, "center");

  // Hero polaroid in the middle. If no photo loaded, fall back to a paper
  // square so the layout still looks intentional.
  const pW = 660;
  const pH = 760;
  const pX = (W - pW) / 2;
  const pY = 380;

  // shadow
  ctx.save();
  ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
  ctx.shadowBlur = 28;
  ctx.shadowOffsetY = 14;
  ctx.fillStyle = "#fffaf0";
  ctx.fillRect(pX, pY, pW, pH);
  ctx.restore();

  // photo inset
  const pad = 36;
  const captionPad = 110;
  const ix = pX + pad;
  const iy = pY + pad;
  const iw = pW - pad * 2;
  const ih = pH - pad * 2 - captionPad;
  if (photo) {
    drawImageCover(ctx, photo, ix, iy, iw, ih);
  } else {
    // gentle gradient placeholder
    const sky = ctx.createLinearGradient(0, iy, 0, iy + ih);
    sky.addColorStop(0, "#f5c89a");
    sky.addColorStop(1, "#5d6a8a");
    ctx.fillStyle = sky;
    ctx.fillRect(ix, iy, iw, ih);
  }

  // photo border
  ctx.strokeStyle = "rgba(0, 0, 0, 0.08)";
  ctx.lineWidth = 1;
  ctx.strokeRect(ix + 0.5, iy + 0.5, iw - 1, ih - 1);

  // Subtitle on the polaroid's bottom margin (handwritten caption style).
  ctx.fillStyle = "#3a2c1a";
  ctx.font = `78px ${HAND}`;
  ctx.fillText(sub, pX + pW / 2, iy + ih + captionPad / 2 + 10);

  return toTex(c);
}

// ---------------------------------------------------------------------------
// 2. Inside left: paper page; the actual photos live as 3D Polaroid meshes
//    that are added to the scene by main.ts. We just paint a soft caption
//    here as a hint that things sit on this page.
// ---------------------------------------------------------------------------

function makeInsideLeft(): THREE.Texture {
  const { c, ctx } = newCanvas();
  paperBg(ctx);

  // The polaroids themselves are 3D meshes layered on top of this page in
  // world space. We just leave a tiny handwritten nudge so mom realises the
  // photos are draggable.
  ctx.fillStyle = "rgba(90, 74, 54, 0.55)";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `60px ${HAND}`;
  ctx.fillText("(drag the photos!)", W / 2, H - 90);

  return toTex(c);
}

// ---------------------------------------------------------------------------
// 3. Inside right: the message page.
// ---------------------------------------------------------------------------

function makeInsideRight(to: string, body: string, sign: string): THREE.Texture {
  const { c, ctx } = newCanvas();
  paperBg(ctx);

  // soft gold rule along the spine side
  ctx.fillStyle = "rgba(220, 170, 100, 0.3)";
  ctx.fillRect(46, 80, 4, H - 160);

  ctx.fillStyle = "#3a2c1a";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";

  // greeting
  ctx.font = `116px ${HAND}`;
  ctx.fillText(to, 110, 190);

  // body — bumped from 66 → 78 for legibility on small phone screens. Side
  // margin tightened from 130 → 110 so the wider glyphs still wrap to a
  // similar number of lines as before.
  ctx.font = `78px ${HAND}`;
  ctx.fillStyle = "#4b3a25";
  const used = wrapText(ctx, body, 110, 340, W - 220, 86, "left");

  // signature
  ctx.font = `124px ${HAND}`;
  ctx.fillStyle = "#3a2c1a";
  ctx.fillText(sign, 110, 340 + used + 36);

  return toTex(c);
}

// ---------------------------------------------------------------------------
// 4. Outside back
// ---------------------------------------------------------------------------

function makeOutsideBack(): THREE.Texture {
  const { c, ctx } = newCanvas();
  paperBg(ctx, "#f3ebd9");

  ctx.fillStyle = "rgba(120, 90, 50, 0.65)";
  ctx.font = `54px ${HAND}`;
  ctx.textAlign = "right";
  ctx.textBaseline = "bottom";
  ctx.fillText("with love, 2026", W - 90, H - 90);

  return toTex(c);
}
